import { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from 'uuid';
import * as St from './index.styled';
import CustomBlockButton from "../../../../../components/button/CustomBlockButton";
import { ProductOptionPickerModal } from "../../../../../components/product-option-picker-modal";
import { usePalletStandardRulesHook } from "../../../../../hooks/usePalletStandardRulesHook";
import { useProductOptionsQueryHook } from "../../../../../hooks/useProductOptionsQueryHook";
import { customToast } from "../../../../../components/toast/custom-react-toastify/v1";

function cloneRule(rule) {
    return {
        uuid: rule?.uuid || uuidv4(),
        name: rule?.name || '',
        boxesPerPallet: rule?.boxesPerPallet ?? 20,
        conditions: (rule?.conditions || []).map(c => ({
            productOptionCode: c?.productOptionCode || '',
            productName: c?.productName || '',
            optionName: c?.optionName || '',
        })),
    };
}

// 조건이 속한 "상품" 식별키 (productOptions로 해석, 없으면 상품명 폴백).
// 같은 상품을 공유하는 조건들을 묶어 일괄 제거할 때 사용한다.
function productKeyOfCondition(cond, productOptions) {
    const po = (productOptions || []).find(p => p?.productOptionCode === cond.productOptionCode);
    if (po) {
        if (po.productId != null) {
            return 'id:' + po.productId;
        }
        if (po.productCode) {
            return 'code:' + po.productCode;
        }
    }
    return 'name:' + (cond.productName || '');
}

// 규칙이 검색어(이름/상품명/옵션명/옵션코드)에 걸리는지
function ruleMatchesKeyword(rule, keyword) {
    const t = (keyword || '').trim().toLowerCase();
    if (!t) {
        return true;
    }
    if ((rule.name || '').toLowerCase().includes(t)) {
        return true;
    }
    return (rule.conditions || []).some(c =>
        (c.productName || '').toLowerCase().includes(t)
        || (c.optionName || '').toLowerCase().includes(t)
        || (c.productOptionCode || '').toLowerCase().includes(t));
}

/**
 * 규격 적재 규칙 관리 탭.
 * 특정 상품(군)을 빈패킹 알고리즘/박스 크기와 무관하게 한 파레트에 고정 박스수로 병합 적재한다.
 * 규칙은 workspace 단위로 백엔드에 저장(공유)된다.
 *
 * 규칙 수가 많아질 수 있어 목록은 아코디언(요약 행 → 펼쳐서 편집) + 검색으로 구성한다.
 */
export function WegetStandardRules() {
    const { rules, saveRules, isSaving, isLoading } = usePalletStandardRulesHook();
    const { productOptions } = useProductOptionsQueryHook();
    const [localRules, setLocalRules] = useState([]);
    const loadedRef = useRef(false);
    const [pickerRuleIndex, setPickerRuleIndex] = useState(null);
    const [keyword, setKeyword] = useState('');
    // 펼쳐진 규칙 uuid 집합 (기본은 모두 접힘)
    const [expandedUuids, setExpandedUuids] = useState(() => new Set());

    // 최초 로드 시 1회 서버 규칙을 로컬 편집상태로 복사 (이후 편집 보존)
    useEffect(() => {
        if (!loadedRef.current && !isLoading) {
            setLocalRules((rules || []).map(cloneRule));
            loadedRef.current = true;
        }
    }, [isLoading, rules]);

    const toggleExpand = (uuid) => {
        setExpandedUuids(prev => {
            const next = new Set(prev);
            if (next.has(uuid)) {
                next.delete(uuid);
            } else {
                next.add(uuid);
            }
            return next;
        });
    };
    const expandAll = () => setExpandedUuids(new Set(localRules.map(r => r.uuid)));
    const collapseAll = () => setExpandedUuids(new Set());

    const handleAddRule = () => {
        const uuid = uuidv4();
        setLocalRules(prev => [...prev, { uuid, name: '', boxesPerPallet: 20, conditions: [] }]);
        // 새 규칙은 바로 펼쳐 편집할 수 있게 하고, 검색 필터에 가려지지 않도록 검색어 해제
        setExpandedUuids(prev => new Set(prev).add(uuid));
        setKeyword('');
    };
    const handleDeleteRule = (ruleIdx) => {
        setLocalRules(prev => prev.filter((_, i) => i !== ruleIdx));
    };
    const handleChangeRuleField = (ruleIdx, field, value) => {
        setLocalRules(prev => prev.map((r, i) => i === ruleIdx ? { ...r, [field]: value } : r));
    };
    const handleAddConditions = (ruleIdx, productOptions) => {
        setLocalRules(prev => prev.map((r, i) => {
            if (i !== ruleIdx) {
                return r;
            }
            const existingCodes = new Set(r.conditions.map(c => c.productOptionCode));
            const additions = [];
            (productOptions || []).forEach(po => {
                const productOptionCode = po?.productOptionCode || '';
                if (!productOptionCode || existingCodes.has(productOptionCode)) {
                    return;
                }
                existingCodes.add(productOptionCode);
                additions.push({
                    productOptionCode,
                    productName: po?.productName || '',
                    optionName: po?.productOptionName || '',
                });
            });
            if (additions.length === 0) {
                return r;
            }
            return { ...r, conditions: [...r.conditions, ...additions] };
        }));
    };

    // 조건 상품과 같은 상품(productId)을 공유하는 모든 옵션을 한 번에 추가한다.
    const handleAddSiblingOptions = (ruleIdx, cond) => {
        if (!productOptions || productOptions.length === 0) {
            customToast.error('상품 목록을 아직 불러오지 못했습니다. 잠시 후 다시 시도하세요.');
            return;
        }

        const self = productOptions.find(po => po?.productOptionCode === cond.productOptionCode);
        let siblings;
        if (self && self.productId != null) {
            siblings = productOptions.filter(po => po?.productId === self.productId);
        } else if (self && self.productCode) {
            siblings = productOptions.filter(po => po?.productCode === self.productCode);
        } else {
            // 폴백: 상품명으로 매칭
            siblings = productOptions.filter(po => (po?.productName || '') === (cond.productName || ''));
        }

        if (!siblings || siblings.length === 0) {
            customToast.error('같은 상품의 다른 옵션을 찾지 못했습니다.');
            return;
        }
        handleAddConditions(ruleIdx, siblings);
    };
    const handleRemoveCondition = (ruleIdx, condIdx) => {
        setLocalRules(prev => prev.map((r, i) => i === ruleIdx
            ? { ...r, conditions: r.conditions.filter((_, j) => j !== condIdx) }
            : r));
    };

    // 조건 상품과 같은 상품에 속한 규칙 내 모든 옵션 조건을 한 번에 제거한다.
    const handleRemoveSiblingConditions = (ruleIdx, cond) => {
        const targetKey = productKeyOfCondition(cond, productOptions);
        const current = localRules[ruleIdx];
        const removedCount = current
            ? current.conditions.filter(c => productKeyOfCondition(c, productOptions) === targetKey).length
            : 0;
        setLocalRules(prev => prev.map((r, i) => i === ruleIdx
            ? { ...r, conditions: r.conditions.filter(c => productKeyOfCondition(c, productOptions) !== targetKey) }
            : r));
        if (removedCount > 1) {
            customToast.success(`연관 옵션 ${removedCount}개를 제거했습니다.`);
        }
    };
    const handleSaveRules = async () => {
        // 유효 규칙만 정제 (박스수 > 0, 옵션코드 조건 1개 이상).
        const cleaned = localRules
            .map(r => ({
                uuid: r.uuid,
                name: (r.name || '').trim(),
                boxesPerPallet: Number(r.boxesPerPallet) || 0,
                conditions: (r.conditions || [])
                    .filter(c => (c.productOptionCode || '').trim())
                    .map(c => ({
                        productOptionCode: c.productOptionCode,
                        productName: c.productName || '',
                        optionName: c.optionName || '',
                    })),
            }))
            .filter(r => r.boxesPerPallet > 0 && r.conditions.length > 0);

        try {
            await saveRules(cleaned);
            setLocalRules(cleaned.map(cloneRule));
            customToast.success('규격 규칙을 저장했습니다.');
        } catch (err) {
            customToast.error('규칙 저장에 실패했습니다.');
        }
    };

    // 원본 인덱스를 유지한 채 검색어로 필터링 (핸들러는 원본 인덱스 기준으로 동작)
    const visibleRules = useMemo(() => (
        localRules
            .map((rule, ruleIdx) => ({ rule, ruleIdx }))
            .filter(({ rule }) => ruleMatchesKeyword(rule, keyword))
    ), [localRules, keyword]);

    const trimmedKeyword = keyword.trim();

    return (
        <St.Container>
            <St.Desc>
                규격 규칙에 등록된 상품(군)은 <b>빈패킹 알고리즘·박스 크기를 무시</b>하고
                한 규칙 안에서 <b>병합해 파레트당 지정한 박스수</b>로 적재됩니다.
                조건은 <b>옵션코드</b>로 매칭됩니다.
                저장 후 <b>불러오기</b>를 실행하면 반영됩니다.
            </St.Desc>

            {localRules.length > 0 &&
                <St.Toolbar>
                    <div className='searchWrap'>
                        <input
                            type='text'
                            inputMode='search'
                            placeholder='규칙명 · 상품명 · 옵션코드 검색'
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                        />
                        {keyword &&
                            <button
                                type='button'
                                className='clear'
                                onClick={() => setKeyword('')}
                                aria-label='검색어 지우기'
                            >
                                ✕
                            </button>
                        }
                    </div>
                    <div className='toolbarMeta'>
                        <span className='count'>
                            {trimmedKeyword
                                ? `${visibleRules.length} / ${localRules.length}개`
                                : `규칙 ${localRules.length}개`}
                        </span>
                        <div className='foldActions'>
                            <button type='button' onClick={expandAll}>모두 펼치기</button>
                            <span className='sep'>·</span>
                            <button type='button' onClick={collapseAll}>모두 접기</button>
                        </div>
                    </div>
                </St.Toolbar>
            }

            {localRules.length === 0 && !isLoading &&
                <St.Empty>등록된 규칙이 없습니다. 아래에서 규칙을 추가하세요.</St.Empty>
            }
            {localRules.length > 0 && visibleRules.length === 0 &&
                <St.Empty>‘{trimmedKeyword}’에 해당하는 규칙이 없습니다.</St.Empty>
            }

            <St.RuleList>
                {visibleRules.map(({ rule, ruleIdx }) => {
                    const isExpanded = expandedUuids.has(rule.uuid);
                    // 같은 상품(옵션 묶음)이 규칙 안에 몇 개나 있는지 — 2개 이상이면 일괄 제거 버튼 노출
                    const productCounts = new Map();
                    rule.conditions.forEach(c => {
                        const k = productKeyOfCondition(c, productOptions);
                        productCounts.set(k, (productCounts.get(k) || 0) + 1);
                    });
                    return (
                        <St.RuleCard key={rule.uuid} className={isExpanded ? 'expanded' : ''}>
                            <div className='summaryRow'>
                                <button
                                    type='button'
                                    className='summaryToggle'
                                    onClick={() => toggleExpand(rule.uuid)}
                                    aria-expanded={isExpanded}
                                >
                                    <span className='caret'>{isExpanded ? '▾' : '▸'}</span>
                                    <span className='summaryName'>
                                        {rule.name || <em className='noName'>(이름 없음)</em>}
                                    </span>
                                    <span className='summaryMeta'>
                                        {rule.boxesPerPallet || 0}박스 · 조건 {rule.conditions.length}
                                    </span>
                                </button>
                                <button
                                    type='button'
                                    className='deleteRule'
                                    onClick={() => handleDeleteRule(ruleIdx)}
                                    aria-label='규칙 삭제'
                                >
                                    🗑
                                </button>
                            </div>

                            {isExpanded &&
                                <div className='ruleBody'>
                                    <input
                                        className='name'
                                        type='text'
                                        placeholder='규칙 이름 (예: 타겟제품1)'
                                        value={rule.name}
                                        onChange={(e) => handleChangeRuleField(ruleIdx, 'name', e.target.value)}
                                    />

                                    <div className='boxRow'>
                                        <span>파레트당 박스수</span>
                                        <input
                                            type='number'
                                            inputMode='numeric'
                                            min={1}
                                            value={rule.boxesPerPallet}
                                            onChange={(e) => handleChangeRuleField(ruleIdx, 'boxesPerPallet', e.target.value)}
                                        />
                                        <span className='unit'>박스 / 파레트</span>
                                    </div>

                                    <St.Conditions>
                                        {rule.conditions.length > 0 &&
                                            <div className='condHeader'>조건 상품 {rule.conditions.length}개</div>
                                        }
                                        {rule.conditions.length === 0 &&
                                            <div className='condEmpty'>조건 상품이 없습니다. 아래 버튼으로 추가하세요.</div>
                                        }
                                        <div className='chipGrid'>
                                            {rule.conditions.map((cond, condIdx) => {
                                                const siblingCount = productCounts.get(productKeyOfCondition(cond, productOptions)) || 1;
                                                return (
                                                    <span className='chip' key={cond.productOptionCode || condIdx}>
                                                        <button
                                                            type='button'
                                                            className='chipSibling'
                                                            title='같은 상품의 다른 옵션 전체 추가'
                                                            aria-label='같은 상품의 다른 옵션 전체 추가'
                                                            onClick={() => handleAddSiblingOptions(ruleIdx, cond)}
                                                        >
                                                            ⊕
                                                        </button>
                                                        <span className='chipLabel'>
                                                            <span className='chipName'>{cond.productName || '-'}</span>
                                                            {cond.optionName &&
                                                                <span className='chipOpt'>{cond.optionName}</span>
                                                            }
                                                            <span className='chipCode'>{cond.productOptionCode}</span>
                                                        </span>
                                                        {siblingCount > 1 &&
                                                            <button
                                                                type='button'
                                                                className='chipSiblingRemove'
                                                                title={`연관 옵션 ${siblingCount}개 모두 제거`}
                                                                aria-label={`연관 옵션 ${siblingCount}개 모두 제거`}
                                                                onClick={() => handleRemoveSiblingConditions(ruleIdx, cond)}
                                                            >
                                                                ⊖
                                                            </button>
                                                        }
                                                        <button
                                                            type='button'
                                                            className='chipRemove'
                                                            onClick={() => handleRemoveCondition(ruleIdx, condIdx)}
                                                            aria-label='조건 삭제'
                                                        >
                                                            ✕
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                            <button
                                                type='button'
                                                className='chipAdd'
                                                onClick={() => setPickerRuleIndex(ruleIdx)}
                                            >
                                                + 조건 상품 추가
                                            </button>
                                        </div>
                                    </St.Conditions>
                                </div>
                            }
                        </St.RuleCard>
                    );
                })}
            </St.RuleList>

            <St.AddRuleButton type='button' onClick={handleAddRule}>
                + 규칙 추가
            </St.AddRuleButton>

            <St.Footer>
                <CustomBlockButton
                    type='button'
                    onClick={() => handleSaveRules()}
                    disabled={isSaving}
                >
                    {isSaving ? '저장 중…' : '규격 규칙 저장'}
                </CustomBlockButton>
            </St.Footer>

            <ProductOptionPickerModal
                open={pickerRuleIndex !== null}
                onClose={() => setPickerRuleIndex(null)}
                multiSelect={true}
                onSelectMany={(productOptions) => handleAddConditions(pickerRuleIndex, productOptions)}
                title='조건 상품 선택 (여러 개 체크 가능)'
            />
        </St.Container>
    );
}
